import { useQuery } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import { UI_THEME_QUERY_KEY } from '@/components/system-ui-theme-provider';
import { getCachedUiTheme } from '@/lib/themes';
import {
  isValidUiTheme,
  getSkinForTheme,
  DEFAULT_UI_THEME,
  type UiThemeId,
  type UiSkinId,
} from '@comp-sys/shared';

/**
 * 响应式读取当前系统 UI 主题与形态家族。
 *
 * 反应性真源：与 {@link SystemUiThemeProvider} 同一个 TanStack Query key（`['system','ui-theme']`）。
 * 管理员在外观设置切换主题时 `setQueryData` 更新缓存 → 本 hook 重渲染。
 *
 * 首屏：API 未返回前用 localStorage 缓存兜底（与 Provider 首屏策略一致），避免 shell 闪烁。
 *
 * 用途：`<AppShell>` 据返回的 `skin` 选择对应形态家族的**结构化** shell。
 * 颜色继续走 `data-theme` CSS、原子控件继续走 `data-skin` CSS；
 * 布局 shell 的组件树/导航范式/内容几何在此按家族分化——这是「换应用 vs 换皮」的分水岭。
 */
export function useActiveUiTheme(): { theme: UiThemeId; skin: UiSkinId } {
  const { data } = useQuery({
    queryKey: UI_THEME_QUERY_KEY,
    queryFn: () => systemApi.getUiThemeConfig(),
    staleTime: 5 * 60 * 1000,
  });

  const cached = getCachedUiTheme();
  const theme: UiThemeId =
    data?.theme && isValidUiTheme(data.theme)
      ? data.theme
      : isValidUiTheme(cached)
        ? (cached as UiThemeId)
        : DEFAULT_UI_THEME;

  return { theme, skin: getSkinForTheme(theme) };
}
